import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../constants/providers/AuthProvider";
import { supabase } from "../lib/supabase";
import { useCurrentCompany } from "./useCurrentCompany";

/**
 * Web time card — ports the core of the mobile clock-in feature
 * (app-mobile clockin.tsx) to the web app. Scope for this PR: clock in/out,
 * current-period hours, and the owner/staff roster of who's on the clock.
 * Deferred to a follow-up (matching mobile): geofence prompt, PDF export,
 * edit-request approvals.
 *
 * Backed by the existing `employee_clockins` + `company_pay_period_settings`.
 */

export interface Clockin {
  id: string;
  user_id: string;
  company_id: string;
  location_id: string;
  clock_in: string;
  clock_out: string | null;
}

export interface LocationLite {
  id: string;
  name: string;
}

export interface PayPeriodSettings {
  period_type: "weekly" | "biweekly" | "semimonthly" | "monthly";
  week_start_dow: number | null;
  biweekly_anchor_date: string | null;
  semimonthly_day1: number | null;
  semimonthly_day2: number | null;
  monthly_day: number | null;
}

const DAY_MS = 86_400_000;

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Current pay-period bounds [start, end) for the four supported period types.
 * Falls back to the calendar month (the mobile #672 fallback) so totals never
 * accumulate across all history when a gym hasn't configured a period yet.
 *
 * NOTE: computed in device-local time, mirroring v1 mobile. The v2 rebuild moves
 * this server-side in the location's timezone — out of scope for this port.
 */
export function currentPeriodBounds(
  settings: PayPeriodSettings | null,
  now = new Date(),
): { start: Date; end: Date } {
  const type = settings?.period_type;

  if (type === "weekly") {
    const dow = settings?.week_start_dow ?? 0;
    const start = startOfDay(now);
    start.setDate(start.getDate() - ((start.getDay() - dow + 7) % 7));
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    return { start, end };
  }

  if (type === "biweekly" && settings?.biweekly_anchor_date) {
    const anchor = startOfDay(new Date(settings.biweekly_anchor_date));
    const cycles = Math.floor(
      (startOfDay(now).getTime() - anchor.getTime()) / (14 * DAY_MS),
    );
    const start = new Date(anchor.getTime() + cycles * 14 * DAY_MS);
    const end = new Date(start.getTime() + 14 * DAY_MS);
    return { start, end };
  }

  if (type === "semimonthly") {
    const d1 = settings?.semimonthly_day1 ?? 1;
    const d2 = settings?.semimonthly_day2 ?? 16;
    const [lo, hi] = d1 <= d2 ? [d1, d2] : [d2, d1];
    const day = now.getDate();
    const y = now.getFullYear();
    const m = now.getMonth();
    if (day >= hi)
      return { start: new Date(y, m, hi), end: new Date(y, m + 1, lo) };
    if (day >= lo)
      return { start: new Date(y, m, lo), end: new Date(y, m, hi) };
    return { start: new Date(y, m - 1, hi), end: new Date(y, m, lo) };
  }

  // Monthly (and the default fallback).
  const day = type === "monthly" ? (settings?.monthly_day ?? 1) : 1;
  const start = new Date(now.getFullYear(), now.getMonth(), day, 0, 0, 0, 0);
  if (now < start) start.setMonth(start.getMonth() - 1);
  const end = new Date(start);
  end.setMonth(start.getMonth() + 1);
  return { start, end };
}

export function hoursBetween(clockIn: string, clockOut: string | null): number {
  const end = clockOut ? new Date(clockOut).getTime() : Date.now();
  return Math.max(0, (end - new Date(clockIn).getTime()) / 3_600_000);
}

/** Everything the Time Card screen needs, scoped to the active company. */
export function useTimeCard() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  // useCurrentCompany exposes the company id as `data`.
  const { data: companyId, role } = useCurrentCompany();
  const isManager = role ? ["owner", "admin"].includes(role) : false;

  const settings = useQuery({
    queryKey: ["pay-period-settings", companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<PayPeriodSettings | null> => {
      if (!companyId) return null;
      const { data } = await supabase
        .from("company_pay_period_settings")
        .select(
          "period_type, week_start_dow, biweekly_anchor_date, semimonthly_day1, semimonthly_day2, monthly_day",
        )
        .eq("company_id", companyId)
        .maybeSingle();
      return (data as PayPeriodSettings) ?? null;
    },
  });

  const locations = useQuery({
    queryKey: ["timecard-locations", companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<LocationLite[]> => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("locations")
        .select("id, name")
        .eq("company_id", companyId)
        .order("name");
      if (error) throw error;
      return (data as LocationLite[]) ?? [];
    },
  });

  const { start, end } = currentPeriodBounds(settings.data ?? null);

  // The caller's OPEN punch — queried WITHOUT period bounds, so an overnight or
  // pre-period-boundary shift still shows as clocked-in and can be clocked out.
  const openPunchQuery = useQuery({
    queryKey: ["my-open-clockin", companyId, userId],
    enabled: !!companyId && !!userId,
    queryFn: async (): Promise<Clockin | null> => {
      if (!companyId || !userId) return null;
      const { data, error } = await supabase
        .from("employee_clockins")
        .select("id, user_id, company_id, location_id, clock_in, clock_out")
        .eq("company_id", companyId)
        .eq("user_id", userId)
        .is("clock_out", null)
        .order("clock_in", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as Clockin) ?? null;
    },
  });

  // The caller's punches within the current pay period (for the hours total).
  const myPunches = useQuery({
    queryKey: ["my-clockins", companyId, userId, start.toISOString()],
    enabled: !!companyId && !!userId,
    queryFn: async (): Promise<Clockin[]> => {
      if (!companyId || !userId) return [];
      const { data, error } = await supabase
        .from("employee_clockins")
        .select("id, user_id, company_id, location_id, clock_in, clock_out")
        .eq("company_id", companyId)
        .eq("user_id", userId)
        .gte("clock_in", start.toISOString())
        .lt("clock_in", end.toISOString())
        .order("clock_in", { ascending: false });
      if (error) throw error;
      return (data as Clockin[]) ?? [];
    },
  });

  // Manager view: every open punch in the company (who's on the clock now).
  const openPunches = useQuery({
    queryKey: ["open-clockins", companyId],
    enabled: !!companyId && isManager,
    queryFn: async (): Promise<Clockin[]> => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("employee_clockins")
        .select("id, user_id, company_id, location_id, clock_in, clock_out")
        .eq("company_id", companyId)
        .is("clock_out", null)
        .order("clock_in", { ascending: false });
      if (error) throw error;
      return (data as Clockin[]) ?? [];
    },
  });

  const periodHours = (myPunches.data ?? []).reduce(
    (sum, p) => sum + hoursBetween(p.clock_in, p.clock_out),
    0,
  );

  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["my-open-clockin"] });
    void qc.invalidateQueries({ queryKey: ["my-clockins"] });
    void qc.invalidateQueries({ queryKey: ["open-clockins"] });
  };

  // The time is client-supplied here, matching the v1 mobile clock-in. (The v2
  // rebuild makes punch timestamps server-authoritative to remove client clock
  // skew from timesheets; that's a v2 property, not claimed for this port.)
  const clockIn = useMutation({
    mutationFn: async (locationId: string) => {
      if (!companyId || !userId) throw new Error("No active company");
      const { error } = await supabase.from("employee_clockins").insert({
        user_id: userId,
        company_id: companyId,
        location_id: locationId,
        clock_in: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const clockOut = useMutation({
    mutationFn: async (punchId: string) => {
      const { error } = await supabase
        .from("employee_clockins")
        .update({ clock_out: new Date().toISOString() })
        .eq("id", punchId)
        .is("clock_out", null);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    companyId,
    isManager,
    locations: locations.data ?? [],
    period: { start, end },
    myPunches: myPunches.data ?? [],
    openPunch: openPunchQuery.data ?? null,
    periodHours,
    openPunches: openPunches.data ?? [],
    isLoading:
      settings.isLoading ||
      locations.isLoading ||
      myPunches.isLoading ||
      openPunchQuery.isLoading,
    clockIn,
    clockOut,
  };
}
